import React, { useState, useEffect } from 'react';

import { SaveOutlined, UserOutlined, LockOutlined, BellOutlined, SafetyOutlined, EditOutlined } from '@ant-design/icons';
import { useAuth } from '@contexts/admin/AuthContext';
import { Avatar, Card, Button, message, Spin, Form, Input, Switch, Tag, Space, Row, Col, Typography, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { profileService } from '@services/admin/profileService';

import TotpSettings from './TotpSettings';

const { Text } = Typography;

export default function ProfileShow() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [basicInfoForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [user, setUser] = useState(null);
  const [notificationPreferences, setNotificationPreferences] = useState([]);
  const [basicInfoOpen, setBasicInfoOpen] = useState(false);

  useEffect(() => {
    if (currentUser?.id) {
      fetchUser();
      fetchNotificationPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const result = await profileService.get(currentUser.id);

      if (result.success && result.data) {
        setUser(result.data);
      } else {
        message.error(result.message || t('common.error.fetch_failed'));
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      message.error(t('common.error.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const openBasicInfoModal = () => {
    basicInfoForm.setFieldsValue({
      name: user.name,
      email: user.email,
    });
    setBasicInfoOpen(true);
  };

  const handleUpdateBasicInfo = async (values) => {
    try {
      setSaving(true);
      const result = await profileService.update(currentUser.id, values);

      if (result.success) {
        message.success(t('admin.profile.success'));
        setBasicInfoOpen(false);
        fetchUser();
      } else {
        message.error(result.message || t('admin.profile.error.update_failed'));
      }
    } catch (error) {
      console.error('Failed to update user:', error);
      message.error(t('admin.profile.error.update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const result = await profileService.getNotificationPreferences();
      if (result.success && result.data?.preferences) {
        setNotificationPreferences(result.data.preferences);
      }
    } catch (error) {
      console.error('Failed to fetch notification preferences:', error);
    }
  };

  const handleToggleNotification = async (notificationType, channel, enabled) => {
    setNotificationPreferences(prev =>
      prev.map(p =>
        p.notification_type === notificationType && p.channel === channel
          ? { ...p, enabled }
          : p
      )
    );

    try {
      const payload = [{ notification_type: notificationType, channel, enabled }];
      const result = await profileService.updateNotificationPreferences(payload);

      if (result.success && result.data?.preferences) {
        setNotificationPreferences(result.data.preferences);
        message.success(t('admin.profile.notifications.update_success'));
      } else {
        setNotificationPreferences(prev =>
          prev.map(p =>
            p.notification_type === notificationType && p.channel === channel
              ? { ...p, enabled: !enabled }
              : p
          )
        );
        message.error(t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to update notification subscription:', error);
      setNotificationPreferences(prev =>
        prev.map(p =>
          p.notification_type === notificationType && p.channel === channel
            ? { ...p, enabled: !enabled }
            : p
        )
      );
      message.error(t('common.error.operation_failed'));
    }
  };

  const handleUpdatePassword = async (values) => {
    try {
      setSavingPassword(true);
      const result = await profileService.updatePassword(currentUser.id, {
        current_password: values.current_password,
        password: values.password,
      });

      if (result.success) {
        message.success(t('admin.profile.password_success_redirect'));
        passwordForm.resetFields();
        setTimeout(() => { window.location.href = '/admin/login'; }, 1000);
      } else {
        message.error(result.message || t('admin.profile.error.password_failed'));
      }
    } catch (error) {
      console.error('Failed to update password:', error);
      message.error(t('admin.profile.error.password_failed'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading || !user) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  const isDisabled = Boolean(user.disabled_at);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      {/* Identity Card */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Avatar size={56} icon={<UserOutlined />} />
          <div style={{ flex: 1 }}>
            <Space size={12} align="center" style={{ marginBottom: 4 }}>
              <Text strong style={{ fontSize: 18 }}>{user.name || '-'}</Text>
              {isDisabled ? (
                <Tag color="error">{t('admin.users.edit.account_status.disabled')}</Tag>
              ) : (
                <Tag color="success">{t('admin.users.edit.account_status.active')}</Tag>
              )}
            </Space>
            <div>
              <Text type="secondary">{user.email || '-'}</Text>
            </div>
          </div>
          <Button icon={<EditOutlined />} onClick={openBasicInfoModal}>
            {t('common.button.edit')}
          </Button>
        </div>
      </Card>

      {/* 2-column Grid */}
      <Row gutter={[16, 16]}>
        {/* Left — 資安 */}
        <Col xs={24} lg={12}>
          <Card title={<Space><LockOutlined />{t('admin.profile.change_password')}</Space>} style={{ marginBottom: 16 }}>
            <Form form={passwordForm} layout="vertical" onFinish={handleUpdatePassword}>
              <Form.Item
                label={t('admin.profile.fields.current_password')}
                name="current_password"
                rules={[{ required: true, message: t('admin.profile.validation.current_password_required') }]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('admin.profile.placeholders.current_password')} />
              </Form.Item>
              <Form.Item
                label={t('admin.profile.fields.new_password')}
                name="password"
                rules={[
                  { required: true, message: t('admin.profile.validation.password_required') },
                  { min: 6, message: t('admin.profile.validation.password_min_length') },
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('admin.profile.placeholders.new_password')} />
              </Form.Item>
              <Form.Item
                label={t('admin.profile.fields.confirm_password')}
                name="confirm_password"
                dependencies={['password']}
                rules={[
                  { required: true, message: t('admin.profile.validation.confirm_password_required') },
                  ({ getFieldValue }) => ({
                    validator(_, v) {
                      return !v || getFieldValue('password') === v
                        ? Promise.resolve()
                        : Promise.reject(new Error(t('admin.profile.validation.password_mismatch')));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} placeholder={t('admin.profile.placeholders.confirm_password')} />
              </Form.Item>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={savingPassword}>
                {t('admin.profile.update_password')}
              </Button>
            </Form>
          </Card>

          <Card title={<Space><SafetyOutlined />{t('admin.totp.settings.title')}</Space>}>
            <TotpSettings />
          </Card>
        </Col>

        {/* Right — 通知 */}
        <Col xs={24} lg={12}>
          {notificationPreferences.length > 0 && (
            <Card title={<Space><BellOutlined />{t('admin.profile.notifications.title')}</Space>}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                {t('admin.profile.notifications.description')}
              </Text>
              <div>
                {notificationPreferences.map((pref) => (
                  <div
                    key={`${pref.notification_type}-${pref.channel}`}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <span>{t(`admin.profile.notifications.types.${pref.notification_type}`)}</span>
                    <Switch
                      checked={pref.enabled}
                      onChange={(checked) => handleToggleNotification(pref.notification_type, pref.channel, checked)}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </Col>
      </Row>

      {/* Edit Basic Info Modal */}
      <Modal
        title={t('admin.profile.basic_info')}
        open={basicInfoOpen}
        onCancel={() => setBasicInfoOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Form form={basicInfoForm} layout="vertical" onFinish={handleUpdateBasicInfo}>
          <Form.Item
            label={t('admin.profile.fields.name')}
            name="name"
            rules={[{ required: true, message: t('admin.profile.validation.name_required') }]}
          >
            <Input placeholder={t('admin.profile.placeholders.name')} />
          </Form.Item>
          <Form.Item
            label={t('admin.profile.fields.email')}
            name="email"
            rules={[
              { required: true, message: t('admin.profile.validation.email_required') },
              { type: 'email', message: t('admin.profile.validation.email_invalid') },
            ]}
          >
            <Input type="email" placeholder={t('admin.profile.placeholders.email')} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setBasicInfoOpen(false)}>{t('common.button.cancel')}</Button>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
                {t('common.button.save')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
